/**
 * Body Scroll Lock Utility
 * Provides easy-to-use functions for blocking and unblocking page scroll
 * Useful for modals, overlays, and other UI components
 */

import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";

class ScrollLockManager {
  constructor() {
    this.lockedElements = new Set();
  }

  /**
   * Lock scroll for a specific element (usually a modal)
   * @param {HTMLElement} element - The element to lock scroll for
   * @param {Object} options - Optional configuration
   */
  lock(element, options = {}) {
    if (!element) {
      console.warn("ScrollLockManager: No element provided for scroll lock");
      return;
    }

    const defaultOptions = {
      reserveScrollBarGap: true,
      allowTouchMove: (el) => {
        // Allow scrolling within the locked element
        while (el && el !== document.body) {
          if (el === element) return true;
          el = el.parentElement;
        }
        return false;
      },
      ...options,
    };

    try {
      disableBodyScroll(element, defaultOptions);
      this.lockedElements.add(element);

      // Add a CSS class for styling purposes
      document.body.classList.add("scroll-locked");

      console.log("🔒 Scroll locked for element:", element);
    } catch (error) {
      console.error("ScrollLockManager: Error locking scroll:", error);
    }
  }

  /**
   * Unlock scroll for a specific element
   * @param {HTMLElement} element - The element to unlock scroll for
   */
  unlock(element) {
    if (!element) {
      console.warn("ScrollLockManager: No element provided for scroll unlock");
      return;
    }

    try {
      enableBodyScroll(element);
      this.lockedElements.delete(element);

      // Remove CSS class if no more locked elements
      if (this.lockedElements.size === 0) {
        document.body.classList.remove("scroll-locked");
      }

      console.log("🔓 Scroll unlocked for element:", element);
    } catch (error) {
      console.error("ScrollLockManager: Error unlocking scroll:", error);
    }
  }

  /**
   * Emergency unlock - clears all scroll locks
   * Useful for cleanup or error recovery
   */
  unlockAll() {
    try {
      clearAllBodyScrollLocks();
      this.lockedElements.clear();
      document.body.classList.remove("scroll-locked");

      console.log("🔓 All scroll locks cleared");
    } catch (error) {
      console.error("ScrollLockManager: Error clearing all scroll locks:", error);
    }
  }

  /**
   * Check if scroll is currently locked
   * @returns {boolean}
   */
  isLocked() {
    return this.lockedElements.size > 0;
  }

  /**
   * Get the number of currently locked elements
   * @returns {number}
   */
  getLockedCount() {
    return this.lockedElements.size;
  }
}

// Create and export a singleton instance
const scrollLockManager = new ScrollLockManager();

// Global singleton setup - attach to window object for global access
if (typeof window !== "undefined") {
  // Check if already exists to prevent overwriting
  if (!window.scrollLockManager) {
    window.scrollLockManager = scrollLockManager;
    console.log("🌐 ScrollLockManager attached to window object");
  }
}

// Helper function to get the global instance
export const getGlobalScrollLockManager = () => {
  if (typeof window !== "undefined" && window.scrollLockManager) {
    return window.scrollLockManager;
  }
  // Fallback to local instance if window access fails
  return scrollLockManager;
};

// Export both the class and the singleton instance
export { ScrollLockManager };
export default scrollLockManager;

// Convenience functions for direct use (use global instance when available)
export const lockScroll = (element, options) => getGlobalScrollLockManager().lock(element, options);
export const unlockScroll = (element) => getGlobalScrollLockManager().unlock(element);
export const unlockAllScroll = () => getGlobalScrollLockManager().unlockAll();
export const isScrollLocked = () => getGlobalScrollLockManager().isLocked();
